import { useState, useCallback } from 'react';
import { logger } from '../../utils/logger';
import fetchOrganizationsFromService from '../../services/Organization/fetchOrganizationsFromService';
import { sortByKey } from '../../utils/sort';
import { useAuth } from '../../hooks/auth';

export interface Organization {
  id: string;
  name: string;
  shortName?: string;
  region?: string;
}

export const useFetchOrganizations = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userInfo, accessToken } = useAuth();

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    logger.info('Fetching organizations ...');
    const started = performance.now();
    const companyId = userInfo?.orgId || '';

    try {
      const orgs = await fetchOrganizationsFromService(accessToken || '', companyId);

      // Sort alphabetically by name
      const sortedOrgs = sortByKey(orgs, 'name', 'asc');
      setOrganizations(sortedOrgs);
      logger.info(`Successfully fetched ${orgs.length} organizations in
      ${(performance.now() - started).toFixed(2)} ms`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);

      // Check if this is a circuit breaker error (repeated auth failures)
      if (msg.includes('You are not authenticated to access migration logs')) {
        setError(
          'You are not authenticated to access migration logs. Please contact your administrator for assistance.'
        );
        logger.error('Circuit breaker triggered - not authenticated for migration logs', {
          error: msg
        });
      } else if (msg.includes('Authentication token expired')) {
        // Don't set error for token expiration - the redirect will handle it
        logger.warn('Token expired during org fetch - redirect in progress');
      } else {
        setError(msg);
        logger.error('Error fetching organizations:', { error: msg });
      }

      // Use empty array as fallback - no mock data
      setOrganizations([]);
      logger.info('Using empty array as fallback due to fetch error');
    } finally {
      setLoading(false);
    }
  }, [accessToken, userInfo]);
  return { organizations, loading, error, fetchOrganizations };
};

export default useFetchOrganizations;
